import type { ListConversationEpisodesParams, ListConversationsParams } from '../model/types';

/**
 * Query keys of the conversations feature. Everything hangs off one root so a tenant switch
 * (`SessionContext.resetTenantScopedQueries`, which resets every non-session query) and the
 * mutations' invalidations can address the whole family at once.
 */
const ROOT = ['conversations'] as const;

export const conversationsKeys = {
  root: ROOT,
  lists: () => [...ROOT, 'list'] as const,
  list: (params: ListConversationsParams) => [...ROOT, 'list', params] as const,
  detail: (conversationId: string) => [...ROOT, 'detail', conversationId] as const,
  /** The live tail of the timeline — the newest window, polled. */
  messagesLatest: (conversationId: string) => [...ROOT, 'messages-latest', conversationId] as const,
  /**
   * The OLDER pages of the timeline (a keyset infinite query; pages go backwards in time), keyed by
   * the cursor they start from — the live tail's `nextBefore`. When the tail window shifts (50 newer
   * messages arrived) the cursor changes and a fresh older-pages query starts from the new boundary,
   * so tail ∪ older is always contiguous (no gap of rows that fell out of the tail).
   */
  messages: (conversationId: string, startBefore: string) =>
    [...ROOT, 'messages', conversationId, startBefore] as const,
  episodes: (conversationId: string, params: ListConversationEpisodesParams) =>
    [...ROOT, 'episodes', conversationId, params] as const,
  /** Customer 360's shortcut — keyed by customer, evicted with the rest on tenant switch. */
  customerConversation: (customerId: string) => [...ROOT, 'customer', customerId] as const,
};
